import { Link, useLocation } from "react-router-dom";
import { ChevronRight, Home } from "lucide-react";

interface BreadcrumbItem {
  label: string;
  href?: string;
}

interface BreadcrumbNavigationProps {
  items?: BreadcrumbItem[];
  className?: string;
}

export const BreadcrumbNavigation = ({ items, className = "" }: BreadcrumbNavigationProps) => {
  const location = useLocation();
  
  // Generate breadcrumbs from current path if items not provided
  const generateBreadcrumbs = (): BreadcrumbItem[] => {
    if (items) return items;
    
    const pathSegments = location.pathname.split('/').filter(Boolean);
    const breadcrumbs: BreadcrumbItem[] = [{ label: 'Home', href: '/' }];
    
    const pathMap: Record<string, string> = {
      'about': 'About',
      'contact': 'Contact',
      'math': 'Math',
      'alphabet': 'Alphabet & Numbers',
      'rhymes': 'Rhymes & Songs',
      'geography': 'Geography',
      'science': 'Science',
      'stories': 'Stories',
      'games': 'Games',
      'brain-teasers': 'Brain Teasers',
      'english-learning': 'English Learning',
      'grammar': 'Grammar',
      'sentence-building': 'Sentence Building',
      'sight-words': 'Sight Words',
      'daily-activities': 'Daily Activities',
      'community-helpers': 'Community Helpers',
      'history': 'History',
      'learn-about-world': 'Learn About World',
      'printables-zone': 'Printables Zone',
      'pricing': 'Pricing',
      'yt-gallery': 'YouTube Gallery',
      'blog': 'Blog',
      'north-america': 'North America',
      'south-america': 'South America',
      'europe': 'Europe',
      'africa': 'Africa',
      'asia': 'Asia',
      'australia': 'Australia',
      'antarctica': 'Antarctica',
      'measurement-basics': 'Measurement Basics',
      'shapes-patterns': 'Colors & Shapes',
      'time-money': 'Time & Money',
      'rhyme-abc': 'ABC Rhyme',
      'rhyme-twinkle': 'Twinkle Twinkle'
    };
    
    let currentPath = '';
    pathSegments.forEach((segment, index) => {
      currentPath += `/${segment}`;
      const label = pathMap[segment] || segment.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
      
      if (index === pathSegments.length - 1) {
        // Last item shouldn't be a link
        breadcrumbs.push({ label });
      } else {
        breadcrumbs.push({ label, href: currentPath });
      }
    });
    
    return breadcrumbs;
  };
  
  const breadcrumbs = generateBreadcrumbs();
  
  if (breadcrumbs.length <= 1) return null;

  // BreadcrumbList JSON-LD for crawlers
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BreadcrumbList",
    itemListElement: breadcrumbs.map((b, i) => ({
      "@type": "ListItem",
      position: i + 1,
      name: b.label,
      ...(b.href ? { item: `https://tinyminds.in${b.href}` } : {}),
    })),
  };

  return (
    <nav className={`flex items-center mb-6 ${className}`} aria-label="Breadcrumb">
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
      />
      <div className="bg-card/50 backdrop-blur-sm border border-border/50 rounded-lg px-4 py-2.5 shadow-sm">
        <ol className="flex items-center space-x-2">
          {breadcrumbs.map((item, index) => (
            <li key={index} className="flex items-center">
              {index > 0 && (
                <ChevronRight className="w-4 h-4 mx-2 text-muted-foreground/60 transition-colors" />
              )}
              {item.href ? (
                <Link 
                  to={item.href} 
                  className="group flex items-center text-sm font-medium text-muted-foreground hover:text-primary transition-all duration-200 hover:scale-105 px-2 py-1 rounded-md hover:bg-accent/50"
                >
                  {index === 0 && (
                    <Home className="w-4 h-4 mr-1.5 text-primary group-hover:text-primary transition-colors" />
                  )}
                  <span className="group-hover:underline underline-offset-2">{item.label}</span>
                </Link>
              ) : (
                <span className="flex items-center text-sm font-semibold text-foreground px-2 py-1 bg-primary/10 rounded-md border border-primary/20">
                  {index === 0 && (
                    <Home className="w-4 h-4 mr-1.5 text-primary" />
                  )}
                  <span>{item.label}</span>
                </span>
              )}
            </li>
          ))}
        </ol>
      </div>
    </nav>
  );
};